import { FACE_DRIVE_KEYS, type FaceDriveKey } from "../inference/ogsGpuFlags.ts";
import { branchApprChannels, parsePbrConfig, RGB_APPR_CHANNELS, type PbrConfig } from "./pbrManifest.ts";
// Parser for the multi_region OGS scene.json.

/** The mesh-conditioning a trunk-less head declares. Only one kind exists. */
export type HeadCond = "local_disp";

export interface DispSpec {
  /** Atlas resolution the decoders take the map at — must equal the mesh's. */
  uvRes: number;
  /** Training removed the head's rigid pose (Kabsch to rest) before the field. */
  rigidAlign: boolean;
  /** soft_clip bound on the fuse INPUT, in mm; 0 = off. */
  clipMm: number;
}

export interface BranchSpec {
  name: string;
  geom: string;
  appr: string;
  /** Absent on a FUSED branch — see `fused`. */
  trunk?: string;
  /** Geom takes `rig_params` directly and emits `code`. Must be EXPLICIT. */
  fused?: boolean;
  /** A TexAvatars `--head_local_disp` head (myra_v16): NO trunk — its code is a
   *  constant baked into geom/appr at export, and the per-frame expression enters
   *  as a per-texel displacement UV MAP the browser rasterises from `verts - rest`
   *  (rig/headDisplacementMap.js). geom takes `disp_map`; appr `plucker` +
   *  `disp_map`. Like `fused`, only this EXPLICIT marker exempts the branch from
   *  needing a trunk. docs/OGS_LOCAL_DISP_HEAD.md. */
  headCond?: HeadCond;
  /** How the disp_map must be built for a `headCond` branch — the run's own
   *  training settings, read by the exporter from cfg_args. Absent otherwise. */
  disp?: DispSpec;
  /** Absent on a gaze-conditioned branch, which has polar-chart statics instead. */
  meshJson?: string;
  meshBin?: string;
  uvRes: number;
  /** The resolution the geom/appr decoders EMIT at. Equal to uvRes unless the bundle
   *  declares `decoder_res` — then the export's terminal Resize (which brought the
   *  512² CNN up to uv_res) has been stripped from the ONNX and the lift applies the
   *  same bilinear filter itself (inference/uvResize.ts). It is part of the model, not
   *  free upsampling: lifting the decoder-res map directly renders needle splats. */
  decoderRes: number;
  /** How this branch's decoders declare their output map (see uvResize.ts). */
  decoderLayout: "nchw" | "nhwc";
  /** `decode` — when this branch's decoders run, overriding what the bundle's
   *  shape implies. Absent (the normal case) leaves it derived; see
   *  `branchExpression`. */
  decode?: BranchExpression;
  rigDim: number;
  /** Additive constant on the decoder's log-scale, applied by the lift. The trainer
   *  adds `log(scale_base) + log(scale_ref_res / uv_res)` AFTER the geom decoder, so
   *  it is NOT in the ONNX and a bundle that omits it renders splats off by e^bias.
   *  0 (the default) is what every pre-v5 bundle needs — their bases were 1 at the
   *  reference res, which is why nothing carried it until now. */
  scaleLogBias: number;
  /** Ceiling on the BIASED log-scale, applied by the lift. The trainer's own geometry
   *  guard (`MeshBranch.forward`) clamps here so a decoder texel that spikes cannot
   *  become an Inf covariance — it has been seen reach log-scale ~38, i.e. e^38. Like
   *  the bias it lives outside the ONNX, so the web has to apply it or a spiking
   *  checkpoint renders a splat the trainer never drew. */
  scaleLogMax: number;
  /** `--sigma_max` for this branch: the BOUNDED scale map (myra v8+). When it is
   *  > 0 the trainer replaces the unbounded `exp(raw + scale_log_bias)` with
   *
   *      sigma = sigma_max * sigmoid(raw + sigmaOffset)
   *
   *  and `scaleLogBias` MUST NOT also be added — the bias is folded into
   *  sigmaOffset and applying both applies it twice. 0 = the unbounded map.
   *
   *  This is not a constant offset, it is a different function, and the two
   *  agree EXACTLY at raw = 0 (the offset is solved to make the map the
   *  identity at initialisation). So a bundle whose map the lifter ignores
   *  looks right at the neutral scale and inflates everything above it — on
   *  myra v10 a texel one unit above init renders 1.4–1.9x too big, three
   *  units above 5–10x, and the ceiling goes from sigma_max (1.9–9.4 m) to
   *  exp(scale_log_max) (20–55 m). That is the oversized-splat artefact, and
   *  no amount of ONNX verification can see it: the map runs after the decoder. */
  sigmaMax: number;
  /** The logit the trainer solved so the bounded map is the identity at init:
   *  `sigmoid(sigmaOffset) = exp(scale_log_bias) / sigma_max`.
   *
   *  Carried as the number the trainer used rather than re-derived here, for the
   *  reason branchScaleLogBias documents — a second copy of a trainer formula
   *  goes stale silently. Meaningless when sigmaMax is 0. */
  sigmaOffset: number;
  /** Triangle-bounded splat clamp — `kappa` in `--tri_size_cap`. A splat's world
   *  extent is projected down so it cannot exceed `triKappa` x its host
   *  triangle's bound. 0 (the default) is OFF and lifts byte-identically to a
   *  pre-clamp bundle.
   *
   *  Same contract as scaleLogBias/scaleLogMax above and for the same reason:
   *  the trainer applies this in `MeshBranch.forward`, AFTER the decoder, so it
   *  is not in the ONNX and a bundle that omits it renders splats the trained
   *  model never had. Unlike those two there is no sane fallback constant to
   *  guess — a wrong kappa is a different model — so an undeclared value means
   *  OFF, never a copied trainer number. */
  triKappa: number;
  /** Sliver rejection — `--sliver_min_quality` for this branch. Faces whose
   *  shape quality sqrt(area)/(0.658*longest_edge) falls below this host no
   *  Gaussian, and `triangleBound` derates over [q, 5q]. 0 = OFF.
   *
   *  PER BRANCH because the quality distributions differ by a lot: measured p50
   *  is 0.722 head / 0.734 body / 0.686 clothes but 0.402 hair, whose cards are
   *  legitimately elongated strips. One global threshold either misses slivers
   *  on skin or derates a fifth of the hair. */
  sliverQMin: number;
  /** Training's --{branch}_uv_erode radius, 0 when absent.
   *
   *  The lifter min-pools the RAW validity mask by this before pass 2's keep
   *  test, and leaves pass 1's identity-Jacobian test on the raw mask — the two
   *  are different masks in training and collapsing them measurably corrupts
   *  chart-edge splats. */
  uvErode: number;
  rig2mesh?: string; // per-branch live rig→verts ONNX
  /** The branch's RIG PARAMS file (`rig_params_<branch>.npy`): the training-frame rig
   *  vectors, one row per pose. Row 0 is the reference pose the branch's
   *  `neutral_vertices` were exported at, and therefore the only rig that decodes a
   *  neutral — an all-zero vector is not one (see BranchAssets.referenceRig).
   *
   *  NOT the rig board's pose presets (`rigBoardSource.presets`, the top-level
   *  `scene.rig_presets` the pose player reads). These are per-branch decoder inputs, and
   *  the two must not be conflated — the bundle just happens to spell the per-branch key
   *  `rig_presets` too. */
  rigParams?: string;
  /** Planes this branch's appr emits: 3 for RGB, the material width for a PBR
   *  branch. The lift sizes `color_uv` from it — a buffer sized for 3 truncates a
   *  material to its albedo logits and the decode silently reads short. */
  apprChannels: number;
  /** This branch's appr emits a MATERIAL, which a light turns into radiance. */
  pbr?: boolean;
  /** Myra eyes: cond is gaze + head pose and src/ogs/eyes/ renders it, never MultiRegionBranch. */
  gazeConditioned?: boolean;
  statics?: EyeStaticsFiles;
  /** The eyeball mesh whose surface IS the Gaussian positions; the mount refuses if absent. */
  eyeMesh?: { glb: string; json: string };
}

export interface EyeStaticsFiles {
  leftJson: string;
  leftBin: string;
  rightJson: string;
  rightBin: string;
  meta: string;
}

export interface SceneManifest {
  schemaVersion: string;
  subject: string;
  mode: string;
  numPoses: number;
  order: string[]; // deterministic branch order
  branches: Record<string, BranchSpec>;
  /** "head": every branch consumes the SAME live rig vector. Absent: driven independently. */
  bodyRig?: "head";
  /** The part rig poses the face (SyntheticOGSFace): no rig→verts model planned or fetched. */
  syntheticFace: boolean;
  /** Valid rig-control domain; outside it the decoders extrapolate. Legacy default [-3, 3]. */
  rigRange: [number, number];
  /** Bundle verts → scene metres (isaac V2 trains in cm → 0.01). Default 1. */
  worldScale: number;
  /** appr's plücker moment scale: 1 = full, 0 = direction-only (the default). */
  momentScale: number;
  /** Scene placement of the splat, metres. Composes with ANY momentScale: the
   *  plücker paths subtract it from the camera before applying originScale, so
   *  the moment is formed in the splat's local frame (inference/plucker.ts).
   *  It used to require momentScale 0 — direction-only is translation-invariant,
   *  which hid the missing subtraction until eyeline V10-C shipped a non-zero
   *  offset WITH momentScale 1. */
  worldOffset: [number, number, number];
  /** Orientation of the TRAINING frame relative to the scene's, XYZ Euler
   *  degrees — undone at render time so the avatar stands upright (myra_v5
   *  trains 180° about Z and renders upside down without it). Applied by
   *  render/worldRotation.ts to BOTH the SplatMesh and, inverted, the plücker
   *  camera. Default [0,0,0] = render in the trained frame. */
  worldRotation: [number, number, number];
  /** Which of the two EYE-REGION drives this bundle can take, when it says so.
   *
   *  A drive is a property of the CAPTURE, not of the session: the eye aim writes six
   *  controls linearly and the blink macro drives 14 lid controls toward a trained closed
   *  row, and a thin pose table has never contained those combinations — on
   *  myragaussainsplatting's 30-row table each blows the face out on its own. That is the
   *  same fact for every viewer of that bundle, so it belongs in the bundle rather than in
   *  a URL every one of them has to know to type.
   *
   *  A key ABSENT means the bundle does not state it, which is not the same as `true`: the
   *  default stays on, but an explicit `?gaze=`/`?blink=` outranks a declaration either
   *  way (resolveFaceDrives). Re-export a bundle with a richer pose table and the right
   *  fix is to drop the key, not to flip it. */
  faceDrives: Partial<Record<FaceDriveKey, boolean>>;
  /** The bundle's BecomingLit declaration, or null when every branch emits RGB. */
  pbr: PbrConfig | null;
}

// Canonical order; a branch not listed here is appended in its object-key order.
export const BRANCH_ORDER = ["head", "body", "teeth", "clothes", "eyes"];

// The trainer's geometry-guard ceilings on the biased log-scale
// (multi_part_gaussian_model.py's _SCALE_LOG_MAX / _CLOTHES_SCALE_LOG_MAX). Clothes is
// tighter because its decoder was the one seen spiking.
const SCALE_LOG_MAX = 4.0;
const CLOTHES_SCALE_LOG_MAX = 3.0;

/** The ceiling on this branch's biased log-scale.
 *
 *  THE FALLBACK IS NO CEILING, and that is a compatibility rule, not an oversight: every
 *  bundle shipped before the bias existed lifted unclamped, so defaulting to the trainer
 *  constant would start shrinking splats in isaac / eyeline / myra-ogs — a silent change
 *  to characters nobody touched. A ceiling only applies when the bundle asks for one:
 *  `scale_log_max` if declared, else the trainer's value for a branch that declares a
 *  `scale_log_bias` (that key is what marks a v5-era export, and those ARE the exports
 *  training clamped). Declaring the ceiling explicitly is better than relying on the
 *  constant here — a copied trainer number goes stale silently.
 *
 *  Typed check, not a coercion: `Number(null)` is 0, which is finite, and a ceiling of 0
 *  would clamp EVERY splat to e^0 — so a bundle with `"scale_log_max": null` would render
 *  a uniform field of unit splats rather than fall back. */
/** The bias to add to this branch's decoder log-scale, 0 (a no-op) when undeclared.
 *
 *  Carried as the DERIVED number the trainer used, not as scale_base + a formula the web
 *  would have to re-derive from uv_res — the eye specular learned what a stale second
 *  copy of a trainer formula costs. A malformed value falls back to the no-op rather than
 *  to NaN, which would make exp() NaN and drop every splat in the branch. */
function branchScaleLogBias(rawBranch: Record<string, unknown>): number {
  const declared = rawBranch.scale_log_bias;
  return typeof declared === "number" && Number.isFinite(declared) ? declared : 0;
}

/** `decoder_res`: the resolution the decoders emit at, uv_res when undeclared.
 *
 *  Declared only by a bundle whose decoder ONNX has had the terminal Resize stripped
 *  (tools/ogs_strip_terminal_resize.py). Typed check: `Number(null)` is 0 and would
 *  size every buffer to nothing. A value ABOVE uv_res is refused — the lift only
 *  upsamples, which is the direction the export's Resize took. */
/** `decoder_layout: "nhwc"` — the export was re-declared by
 *  tools/ogs_decoder_output_layout.py so the WebGPU EP needs no output transpose.
 *  Anything else, including a malformed value, is the shipped NCHW: a typo must
 *  never make the lift read a map in a layout the decoder did not emit. */
function branchDecoderLayout(name: string, rawBranch: Record<string, unknown>): "nchw" | "nhwc" {
  const v = rawBranch["decoder_layout"];
  if (v === undefined || v === null) return "nchw";
  if (v === "nhwc" || v === "nchw") return v;
  console.warn(`[ogs] branch '${name}': decoder_layout ${JSON.stringify(v)} is not "nchw" or "nhwc" — using nchw`);
  return "nchw";
}

/** `decode: "static"` — this branch renders one pose for the session whatever the
 *  rig does. Anything else, including a malformed value, leaves it UNDECLARED so
 *  the engine's inference stands: same rule as decoder_layout above, and for the
 *  same reason — a typo must not silently freeze a branch that should animate,
 *  nor animate one the exporter pinned. */
function branchDecode(name: string, rawBranch: Record<string, unknown>): BranchExpression | undefined {
  const v = rawBranch["decode"];
  if (v === undefined || v === null) return undefined;
  if (EXPRESSIONS.includes(v as BranchExpression)) return v as BranchExpression;
  console.warn(`[ogs] branch '${name}': decode ${JSON.stringify(v)} is not one of `
    + `${EXPRESSIONS.join(" / ")} — deriving it instead`);
  return undefined;
}

function branchDecoderRes(name: string, rawBranch: Record<string, unknown>): number {
  const uvRes = Number(rawBranch.uv_res);
  const declared = rawBranch.decoder_res;
  if (declared === undefined) return uvRes;
  if (typeof declared !== "number" || !Number.isInteger(declared) || declared <= 0) {
    throw new Error(`scene.json branch '${name}' bad decoder_res ${String(declared)}`);
  }
  if (declared > uvRes) throw new Error(`scene.json branch '${name}' decoder_res ${declared} exceeds uv_res ${uvRes}`);
  return declared;
}

/** `tri_size_cap` / `sliver_min_quality` for one branch, 0 (OFF) when absent.
 *
 *  NO trainer-constant fallback, deliberately — unlike branchScaleLogMax, which
 *  can fall back because its constant is an anti-Inf backstop that only ever
 *  catches pathological spikes. These two SELECT AND RESIZE the splat set, so a
 *  guessed value is a silently different model. Absent means the bundle was
 *  exported from a checkpoint trained without the flags, and OFF is then the
 *  bit-identical answer.
 *
 *  Typed check, not a coercion, for the reason branchScaleLogMax documents:
 *  Number(null) is 0 and would read as a deliberate OFF rather than as garbage.
 */
function branchTriClamp(rawBranch: Record<string, unknown>, key: string): number {
  const declared = rawBranch[key];
  return typeof declared === "number" && Number.isFinite(declared) && declared > 0
    ? declared : 0;
}

/** `sigma_offset`, or 0 when undeclared.
 *
 *  0 is a LEGITIMATE value (myra v10's hair solves to 0.00033), so this cannot
 *  use branchTriClamp's `> 0` test. It is only ever read when sigmaMax > 0, and
 *  a bundle that declares sigma_max without an offset is malformed — 0 then
 *  means "no offset", which is the identity map about sigma_max/2 rather than a
 *  silent NaN. */
function branchSigmaOffset(rawBranch: Record<string, unknown>): number {
  const declared = rawBranch.sigma_offset;
  return typeof declared === "number" && Number.isFinite(declared) ? declared : 0;
}

function branchScaleLogMax(name: string, rawBranch: Record<string, unknown>): number {
  const declared = rawBranch.scale_log_max;
  if (typeof declared === "number" && Number.isFinite(declared)) return declared;
  if (typeof rawBranch.scale_log_bias !== "number") return Infinity;
  return name === "clothes" ? CLOTHES_SCALE_LOG_MAX : SCALE_LOG_MAX;
}

/** Only what a schema_version 1 bundle can state; scale/offset/plücker are NOT derivable. */
export interface SingleRegionSceneOptions {
  uvRes: number;
  rigDim: number;
  subject?: string;
  hasRig2Mesh?: boolean;
}

/** A single-region bundle as a one-branch fused scene, in RAW snake_case json. */
export function singleRegionScene(options: SingleRegionSceneOptions): Record<string, unknown> {
  const { uvRes, rigDim } = options;
  if (!Number.isFinite(uvRes) || uvRes <= 0) throw new Error(`singleRegionScene: bad uv_res ${uvRes}`);
  if (!Number.isFinite(rigDim) || rigDim <= 0) throw new Error(`singleRegionScene: bad rig_dim ${rigDim}`);
  return {
    schema_version: "2",
    subject: options.subject ?? "single-region",
    mode: "multi_region",
    body_rig: "head",
    rig_dim: rigDim,
    num_poses: 0,
    branches: {
      head: {
        fused: true,
        geom: "geom.onnx",
        appr: "appr.onnx",
        mesh_json: "mesh.json",
        mesh_bin: "mesh.bin",
        uv_res: uvRes,
        rig_dim: rigDim,
        // A schema_version 1 bundle cannot state a scale_log_bias (same reason it
        // omits world_scale); the parser's 0 default is the pre-v5 behaviour.
        ...(options.hasRig2Mesh ? { rig2mesh: "rig2mesh.onnx" } : {}),
      },
    },
  };
}

/** Polar-chart statics and an eyeball mesh, no mesh atlas; rigDim 0 — it is not rig-driven. */
function gazeConditionedBranch(
  name: string,
  rawBranch: Record<string, unknown>,
  models: { geom: string; appr: string; trunk?: string },
): BranchSpec {
  const uvRes = Number(rawBranch.uv_res);
  if (!Number.isFinite(uvRes)) throw new Error(`scene.json branch '${name}' bad uv_res`);
  const glb = rawBranch.mesh_glb;
  const json = rawBranch.mesh_json;
  return {
    name,
    ...models,
    uvRes,
    decoderRes: uvRes,
    decoderLayout: "nchw",
    rigDim: 0,
    // Inert here — the eye branch lifts off a polar chart, never this UV atlas, and its
    // scale comes from eye_meta. Resolved by the same rule anyway, so there is one.
    scaleLogBias: branchScaleLogBias(rawBranch),
    scaleLogMax: branchScaleLogMax(name, rawBranch),
    sigmaMax: branchTriClamp(rawBranch, "sigma_max"),
    sigmaOffset: branchSigmaOffset(rawBranch),
    triKappa: branchTriClamp(rawBranch, "tri_size_cap"),
    sliverQMin: branchTriClamp(rawBranch, "sliver_min_quality"),
    uvErode: branchTriClamp(rawBranch, "uv_erode"),
    apprChannels: RGB_APPR_CHANNELS,
    gazeConditioned: true,
    eyeMesh: typeof glb === "string" && typeof json === "string" ? { glb, json } : undefined,
    statics: eyeStatics(name, rawBranch.statics),
  };
}

// All five are required: a chart the lift cannot read renders nothing at all.
function eyeStatics(name: string, raw: unknown): EyeStaticsFiles {
  const declared = (raw ?? {}) as Record<string, unknown>;
  const requireFile = (key: string) => {
    const value = declared[key];
    if (typeof value !== "string" || !value) {
      throw new Error(`scene.json branch '${name}' missing statics.${key}`);
    }
    return value;
  };
  return {
    leftJson: requireFile("left_json"),
    leftBin: requireFile("left_bin"),
    rightJson: requireFile("right_json"),
    rightBin: requireFile("right_bin"),
    meta: requireFile("meta"),
  };
}

/** A scene-level 3-vector field (world_offset / world_rotation), or `fallback`
 *  when it isn't three finite numbers — a malformed one is not half-applied. */
function readTriple(raw: unknown, fallback: [number, number, number]): [number, number, number] {
  if (!Array.isArray(raw) || raw.length !== 3) return fallback;
  if (!raw.every((component) => Number.isFinite(Number(component)))) return fallback;
  return [Number(raw[0]), Number(raw[1]), Number(raw[2])];
}

export function isMultiRegionScene(json: unknown): boolean {
  const scene = json as { mode?: string; branches?: unknown } | null;
  return !!scene && scene.mode === "multi_region"
    && !!scene.branches && typeof scene.branches === "object";
}

export function parseSceneManifest(json: unknown): SceneManifest {
  const scene = json as Record<string, unknown>;
  if (!isMultiRegionScene(scene)) throw new Error("scene.json is not a multi_region manifest");
  const rawBranches = scene.branches as Record<string, Record<string, unknown>>;
  const rigDims = (scene.rig_dims ?? {}) as Record<string, number>;
  const trunks = (scene.trunks ?? {}) as Record<string, unknown>;

  const declared = Object.keys(rawBranches);
  const order = [
    ...BRANCH_ORDER.filter((branch) => declared.includes(branch)),
    ...declared.filter((branch) => !BRANCH_ORDER.includes(branch)),
  ];

  const branches: Record<string, BranchSpec> = {};
  for (const name of order) {
    const rawBranch = rawBranches[name];
    const requireString = (key: string) => {
      const value = rawBranch[key];
      if (typeof value !== "string" || !value) {
        throw new Error(`scene.json branch '${name}' missing '${key}'`);
      }
      return value;
    };
    // Two per-branch declaration sites: `branch.trunk` and the scene-level
    // `trunks` map. Some schema_version 2 bundles (eyeline_v6) carry ONLY the
    // map, and reading just the per-branch key rejects them outright
    // ("scene.json branch 'head' missing 'trunk'"). Same rule as the streaming
    // server's server/scene_schema.py:resolve_branch_trunk — keep them in step
    // (tests/fixtures/scene-trunk-cases.json is driven from both suites).
    // The legacy singular top-level `scene.trunk` (write_manifest.py) is read by
    // neither; add it to both together if a bundle ever needs it.
    // A FUSED branch has no trunk by construction (its geom takes rig_params and
    // emits `code` itself). Only an explicit marker gets the exemption, so a
    // manifest that merely forgot its trunk still fails loudly.
    // A --head_local_disp head has no trunk either: its code is baked into the
    // decoders and the expression rides the disp_map. Explicit marker, same rule.
    const headCond: HeadCond | undefined = rawBranch.head_cond === "local_disp" ? "local_disp" : undefined;
    const requireTrunk = (): string | undefined => {
      const own = rawBranch.trunk;
      if (typeof own === "string" && own) return own;
      const shared = trunks[name];
      if (typeof shared === "string" && shared) return shared;
      if (rawBranch.fused === true || headCond) return undefined;
      throw new Error(
        `scene.json branch '${name}' missing 'trunk' (neither branch.trunk nor trunks['${name}'])`,
      );
    };
    if (rawBranch.gaze_conditioned === true) {
      branches[name] = gazeConditionedBranch(name, rawBranch, {
        geom: requireString("geom"),
        appr: requireString("appr"),
        trunk: requireTrunk(),
      });
      continue;
    }
    branches[name] = {
      name,
      geom: requireString("geom"),
      appr: requireString("appr"),
      trunk: requireTrunk(),
      fused: rawBranch.fused === true,
      ...(headCond ? { headCond, disp: branchDisp(rawBranch, Number(rawBranch.uv_res)) } : {}),
      meshJson: requireString("mesh_json"),
      meshBin: requireString("mesh_bin"),
      uvRes: Number(rawBranch.uv_res),
      decoderRes: branchDecoderRes(name, rawBranch),
      decoderLayout: branchDecoderLayout(name, rawBranch),
      decode: branchDecode(name, rawBranch),
      // The rig width is PER BRANCH (myra_v10: head 168, the rest 2397). A
      // shared-rig export writes ONE top-level scalar instead, which is still
      // read — isaac V2 ships that way and is genuinely shared — but in a
      // multi-branch scene it is a guess inherited from whichever branch the
      // exporter had in mind, so it is reported. loadBranch overrides it from
      // the branch's own rig params, which is the only per-branch ground truth.
      rigDim: Number(rawBranch.rig_dim ?? rigDims[name] ?? scene.rig_dim),
      scaleLogBias: branchScaleLogBias(rawBranch),
      scaleLogMax: branchScaleLogMax(name, rawBranch),
      sigmaMax: branchTriClamp(rawBranch, "sigma_max"),
      sigmaOffset: branchSigmaOffset(rawBranch),
      triKappa: branchTriClamp(rawBranch, "tri_size_cap"),
      sliverQMin: branchTriClamp(rawBranch, "sliver_min_quality"),
      uvErode: branchTriClamp(rawBranch, "uv_erode"),
      apprChannels: RGB_APPR_CHANNELS,
      rig2mesh: typeof rawBranch.rig2mesh === "string" ? rawBranch.rig2mesh : undefined,
      rigParams: typeof rawBranch.rig_presets === "string" ? rawBranch.rig_presets : undefined,
    };
    if (!Number.isFinite(branches[name].uvRes)) throw new Error(`scene.json branch '${name}' bad uv_res`);
    if (!Number.isFinite(branches[name].rigDim)) throw new Error(`scene.json branch '${name}' bad rig_dim`);
    const declaresOwnRigDim = rawBranch.rig_dim !== undefined || rigDims[name] !== undefined;
    if (!declaresOwnRigDim && order.length > 1) {
      console.warn(`[ogs] scene.json branch '${name}' declares no rig_dim and no rig_dims['${name}'] `
        + `— inheriting the top-level ${branches[name].rigDim}. Rig width is per branch; verify it.`);
    }
  }

  // Parsed AFTER the branches so a malformed block throws with the branch names
  // already resolved; every branch then takes its own width from it.
  const pbr = parsePbrConfig(scene.pbr);
  for (const name of order) {
    const spec = branches[name];
    if (!spec) continue;
    spec.apprChannels = branchApprChannels(pbr, name);
    if (spec.apprChannels !== 3) spec.pbr = true;
  }
  const declaredPbr = pbr ? pbr.branches.filter((b) => !branches[b]) : [];
  if (declaredPbr.length) {
    throw new Error(`scene.json pbr: branches ${declaredPbr.join(", ")} are not in this scene`);
  }

  const rawRigRange = scene.rig_range;
  const rigRange: [number, number] = Array.isArray(rawRigRange) && rawRigRange.length === 2
    && Number.isFinite(Number(rawRigRange[0])) && Number.isFinite(Number(rawRigRange[1]))
    ? [Number(rawRigRange[0]), Number(rawRigRange[1])] : [-3, 3];
  const worldScale = Number.isFinite(Number(scene.world_scale)) && Number(scene.world_scale) > 0
    ? Number(scene.world_scale) : 1;
  // Default 0 (direction-only); 0 is also a VALID declared value, so never a falsy check.
  const momentScale = Number.isFinite(Number(scene.moment_scale)) ? Number(scene.moment_scale) : 0;
  const worldOffset = readTriple(scene.world_offset, [0, 0, 0]);
  const worldRotation = readTriple(scene.world_rotation, [0, 0, 0]);
  // Only an explicit boolean counts. A malformed value leaves the key unstated rather
  // than reading as `false`, so a typo cannot silently switch a drive off.
  const rawDrives = (scene.face_drives ?? {}) as Record<string, unknown>;
  const faceDrives: Partial<Record<FaceDriveKey, boolean>> = {};
  for (const key of FACE_DRIVE_KEYS) {
    if (typeof rawDrives[key] === "boolean") faceDrives[key] = rawDrives[key] as boolean;
  }
  return {
    schemaVersion: String(scene.schema_version ?? ""),
    subject: String(scene.subject ?? ""),
    mode: String(scene.mode),
    numPoses: Number(scene.num_poses ?? 0),
    order,
    branches,
    bodyRig: scene.body_rig === "head" ? "head" : undefined,
    syntheticFace: scene.synthetic_face === true,
    rigRange,
    worldScale,
    momentScale,
    faceDrives,
    worldOffset,
    worldRotation,
    pbr,
  };
}

/** The fp16 sibling of a decoder filename, or null when it isn't an .onnx. Idempotent. */
export function fp16NameFor(file: string | undefined | null): string | null {
  if (typeof file !== "string" || !file.endsWith(".onnx")) return null;
  const stem = file.slice(0, -".onnx".length);
  if (stem.endsWith("_fp16")) return file;
  return `${stem}_fp16.onnx`;
}

/** The per-branch fp16 decoders; a bundle without them loads fp32, no warning. */
export function multiRegionOptionalFileList(sceneJson: unknown): string[] {
  const manifest = parseSceneManifest(sceneJson);
  const files: string[] = [];
  for (const name of manifest.order) {
    const branch = manifest.branches[name];
    appendFiles(files, [fp16NameFor(branch.geom), fp16NameFor(branch.appr)]);
  }
  return files;
}

/** The per-branch rig params (`rig_params_<branch>.npy`), whose row 0 is the rest pose the
 *  neutral decode needs. Kept apart from the fp16 list because that one also decides
 *  `preferFp16`, and fetched eagerly rather than lazily: they must be RESIDENT in the pack
 *  mount before loadMultiRegionScene reads them, and a mount miss is a 404 that silently
 *  drops the branch back to a zero rig. */
export function multiRegionRigParamsList(sceneJson: unknown): string[] {
  const manifest = parseSceneManifest(sceneJson);
  const files: string[] = [];
  for (const name of manifest.order) appendFiles(files, [manifest.branches[name].rigParams]);
  return files;
}

/** The REQUIRED eager fetch set. Excludes frame_vertices: the live rig supplies verts.
 *
 * A branch's `rig2mesh` is never REQUIRED. The exact MetaHuman rig (OpenRigLogic
 * + the character's own DNA, ONE asset for the whole head) can replace the distilled
 * per-branch ONNX, which cost ~74 MB EACH and was the largest thing in a bundle.
 * It survives as the FALLBACK rig — for a character that ships no DNA, and for a
 * branch whose exact rig fails to build — so it is an OPTIONAL fetch, listed by
 * rig2MeshFileNames and resolved by the loader's rig mode. A character with a
 * pack downloads none of it.
 */
export function multiRegionFileList(sceneJson: unknown): string[] {
  const manifest = parseSceneManifest(sceneJson);
  const files = ["scene.json", "export_manifest.json"];
  // The bundle-level NeuralBSDF. Without it a PBR branch shades at refl = 1,
  // which is ~1/5 of the diffuse — dark, chroma-less skin.
  if (manifest.pbr) appendFiles(files, [manifest.pbr.bsdf]);
  for (const name of manifest.order) {
    const branch = manifest.branches[name];
    appendFiles(files, [branch.meshJson, branch.meshBin, branch.trunk, branch.geom, branch.appr,
      ...eyeStaticsFiles(branch)]);
  }
  return files;
}

/** True when this bundle has a rig→vertices STEP at all — i.e. whether a missing
 *  rig means a frozen head or means nothing.
 *
 *  Asked STRUCTURALLY, about the branch's own shape, not about whether it
 *  declares a `rig2mesh`. That distinction is the whole point now: rig2mesh used
 *  to be the only rig, so "declares one" and "needs one" were the same question —
 *  but the exact MetaHuman rig makes a bundle exported WITHOUT any rig2mesh a
 *  normal, desirable thing (it is the ~74 MB per branch this change deletes).
 *  Keyed on the declaration, such a bundle answers "needs no rig", so a character
 *  with no DNA would render a frozen head and show no pill, and rigMode would
 *  explain it as a fused geom that needs none — while the branch is not fused at
 *  all. Keyed on the structure, both stay right no matter what the bundle ships.
 *
 *  The structure is exactly this: a FUSED branch takes `rig_params` straight into
 *  geom and emits `code`, so the controls reach the decoder with no rig→verts
 *  step in between and the character animates perfectly well with no DNA. Every
 *  other branch has that step and needs something to fill it. A single-region
 *  bundle is fused (singleRegionScene sets it), which is why it must never be
 *  told its head is frozen — a warning that fires on a working avatar teaches
 *  people to ignore the warning.
 *
 *  Tolerant by design: this only decides whether to show a warning, so a
 *  malformed manifest answers "no" rather than throwing and taking the whole
 *  bundle load down with it. A genuinely broken scene fails loudly elsewhere. */
export function sceneNeedsRigDeform(sceneJson: unknown): boolean {
  const branches = (sceneJson as { branches?: Record<string, unknown> } | null)?.branches;
  if (!branches || typeof branches !== "object") return false;
  return Object.values(branches).some(
    (b) => !!b && typeof b === "object" && (b as Record<string, unknown>).fused !== true,
  );
}

/** The width of the LIVE control space — the rig a face graph pushes.
 *
 *  The HEAD branch's, because that is the branch a face rig drives. Rig width is
 *  per branch and a bundle may mix them: myra_v10 ships head 168 (a MetaHuman
 *  control board) alongside body/clothes/hair at 2397 (their own space). The
 *  top-level `rig_dim` is no help there — it is whichever branch the exporter had
 *  in mind. */
export function liveRigDim(manifest: SceneManifest): number {
  const head = manifest.branches.head ?? manifest.branches[manifest.order[0]];
  return head?.rigDim ?? 0;
}

/** The branches that can consume the LIVE control vector, in manifest order.
 *
 *  A branch qualifies when its control space is the SAME WIDTH as the live one
 *  (a vector in another space cannot be zero-padded into this one — zero is an
 *  extreme corner of a [0,1] board, not a rest pose) AND something can turn that
 *  vector into vertices for it: the resolved rig.
 *
 *  `body_rig: "head"` is the export that DECLARES every branch shares the head's
 *  rig (isaac V2, eyeline) and is honoured as such. A bundle that declares
 *  nothing is read from its shape instead, which is what lets a MIXED bundle —
 *  one rig-driven head, the rest in their own space — drive its face while the
 *  other branches hold their own neutral pose. Empty means nothing is live and
 *  the bundle renders a static neutral. */
export function liveRigBranches(manifest: SceneManifest, rig: string): string[] {
  // The DECLARATION is answered first, and a resolved rig is not a precondition for it.
  // Two live bundles have no rig: a single-region one, whose FUSED geom takes rig_params
  // straight in (so resolveRigMode correctly reports 'none' — it needs no rig→verts step
  // at all), and one whose rig failed to resolve, which belongs in the live pipeline
  // rendering a frozen head with the pill that says why. Asking `rig === "none"` first
  // sent both down the static neutral path instead, which for the fused ones is a face
  // that simply stops animating — they are the class that animates WITHOUT a rig.
  if (manifest.bodyRig === "head") return [...manifest.order];
  if (rig === "none") return [];
  const dim = liveRigDim(manifest);
  // A local_disp head declares rig_dim 0 — width 0 is no control space, not a wildcard.
  if (!dim) return [];
  return manifest.order.filter((name) => {
    const branch = manifest.branches[name];
    // A headCond branch consumes a disp field, never the control vector.
    if (branch.headCond) return false;
    // `orl` skins the head from the character's own DNA, so it needs no per-branch
    // file; `rig2mesh` is per branch and a branch without one has no rig→verts step.
    return branch.rigDim === dim && (rig === "orl" || !!branch.rig2mesh);
  });
}

/** Per branch: does it re-decode on every rig update, or once at its own
 *  reference rig?
 *
 *  `decode` in the branch's own manifest entry wins; otherwise a branch is live
 *  exactly when `liveRigBranches` names it. That second clause is the point of
 *  this function. The engine INFERS the same answer from `br.rigDim === headDim`,
 *  which agrees with the router on every shipped bundle at its default rig — but
 *  the two questions are different, and they come apart when the bundle has no
 *  live vector at all. `?rig=orl` on a character with no `orl_pack.bin` resolves
 *  the rig to `none`: the head still MATCHES the live width, so the inference
 *  calls it live and it re-decodes per rig update, while nothing can pose it and
 *  every decode returns the same geometry. The router already knows better — it
 *  asks whether anything can turn the vector into vertices — so the policy is
 *  taken from there.
 *
 *  An empty result (no rig resolved, nothing declared live) is a bundle that
 *  renders ONE pose for the session. That is a supported shape, not a failure. */
/** WHEN a branch's decoders run. ONE vocabulary for both render paths.
 *
 *  This used to be two: the legacy engine took `live | static | disp` and the
 *  next path's `faceDrivePlan` took `trunk | fused | local-disp | frozen`. They
 *  were written a week apart against the same `BranchSpec` and already
 *  disagreed — only one distinguished a fused geom, only the other honoured the
 *  bundle's `decode` key, and each path consulted only its own, so the drift was
 *  silent. This is the richer of the two, and it lives HERE because
 *  docs/OGS_ARCHITECTURE.md §6 forbids legacy importing `src/ogs/pipeline/`
 *  (legacy has to stay deletable) and this module is the shared ground. */
export type BranchExpression = "trunk" | "fused" | "local-disp" | "frozen";

const EXPRESSIONS: readonly BranchExpression[] = ["trunk", "fused", "local-disp", "frozen"];

/** When THIS branch's decoders run, given whether it is on the live control vector.
 *
 *  - `fused`      geom takes `rig_params` directly — it animates with no rig at all.
 *  - `local-disp` the expression IS a per-texel displacement map, so it follows the
 *                 POSE rather than the vector; `liveRigBranches` excludes such a
 *                 branch for exactly that reason, which is why deriving from
 *                 liveness alone would wrongly freeze it.
 *  - `trunk`      the ordinary live branch: re-decode on every rig push.
 *  - `frozen`     decode once at its own reference rig; later frames re-lift, and
 *                 appr re-runs on a settled view.
 *
 *  The bundle's own `decode` key outranks all of it, in either direction. */
export function branchExpression(branch: BranchSpec, live: boolean): BranchExpression {
  if (branch.decode) return branch.decode;
  if (branch.fused) return "fused";
  if (branch.headCond) return "local-disp";
  return live ? "trunk" : "frozen";
}

/** Per branch, in manifest order. Liveness comes from `liveRigBranches` — the
 *  router that asks whether anything can turn the vector into vertices — rather
 *  than from rig width, which answers a different question and comes apart when
 *  no rig resolved. */
export function expressionPlan(
  manifest: SceneManifest,
  rig: string,
): Record<string, BranchExpression> {
  const live = new Set(liveRigBranches(manifest, rig));
  return Object.fromEntries(
    manifest.order.map((name) => [name, branchExpression(manifest.branches[name], live.has(name))]),
  );
}

/** Branch name -> its rig2mesh ONNX, for the branches that declare a LOADABLE
 *  one. The fallback rig's fetch list.
 *
 *  Reads the parsed `rig2mesh` field, so it lists only what this app can
 *  actually load. isaac-ogs-facepalm declares `rig2mesh_hp`, a high-pass
 *  compressed basis (HPBASIS1) there has never been a loader for: that bundle
 *  still NEEDS a rig (sceneNeedsRigDeform says so from its branch shape, which is
 *  why that test no longer looks at these names at all) but cannot be given this
 *  one, and listing the file would download ~74 MB to build a session that
 *  throws. */
export function rig2MeshFileNames(sceneJson: unknown): { name: string; file: string }[] {
  const manifest = parseSceneManifest(sceneJson);
  return manifest.order
    .map((name) => ({ name, file: manifest.branches[name].rig2mesh }))
    .filter((b): b is { name: string; file: string } => !!b.file);
}


/** The disp_map recipe of a `head_cond: "local_disp"` branch. Defaults are the
 *  trainer's own (`displacement_uv_map` at the atlas res, raw field, no clip), so a
 *  bundle that omits the block still builds the field the way an unflagged run
 *  trained — and the exporter always writes it, from cfg_args. */
function branchDisp(rawBranch: Record<string, unknown>, uvRes: number): DispSpec {
  const raw = (rawBranch.disp ?? {}) as Record<string, unknown>;
  const res = Number(raw.uv_res);
  const clip = Number(raw.clip_mm);
  return {
    uvRes: Number.isFinite(res) && res > 0 ? res : uvRes,
    rigidAlign: raw.rigid_align === true,
    clipMm: Number.isFinite(clip) && clip > 0 ? clip : 0,
  };
}

// Deduped: branches share trunks and decoders, and each file is fetched once.
function appendFiles(files: string[], candidates: (string | undefined | null)[]): void {
  for (const candidate of candidates) {
    if (candidate && !files.includes(candidate)) files.push(candidate);
  }
}

/** A gaze-conditioned branch's chart statics plus its eyeball mesh; empty for every other. */
function eyeStaticsFiles(branch: BranchSpec): string[] {
  const { statics, eyeMesh } = branch;
  const files = statics
    ? [statics.leftJson, statics.leftBin, statics.rightJson, statics.rightBin, statics.meta]
    : [];
  if (eyeMesh) files.push(eyeMesh.glb, eyeMesh.json);
  return files;
}
